import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { twMerge } from 'tailwind-merge'
import { Cell, Button } from '@highlight/ui'
import { PencilLine } from 'lucide-react'
import {
  AnimatedHeight,
  ASK_USER_CARD_THEMES,
  AskUserBadge,
  GrowingTextarea,
  OPTION_ROW,
  OPTION_ROW_DASHED,
  OPTION_ROW_IDLE,
  OPTION_ROW_PRESSABLE,
  OPTION_ROW_SURFACE,
  OptionBadge,
  ResolvedAnswer,
  StepProgress,
  useRowDragGuard,
  type AskUserCardProgress,
  type AskUserCardTheme,
} from '../ask-user-card'
import { FadingScrollable } from '../../components/elements/FadingScrollable'
import { useSubmitToolResponse } from '../../hooks/useSubmitMessage'
import { useMessagesStore } from '../../store'
import { parseAskUserQuestionAnswer } from './types'
import type { AskUserQuestionAnswer, AskUserQuestionInput, AskUserQuestionItem } from './types'

/**
 * The blocking half of the ask-user pair (Figma "User Input", 8667-35919). It
 * shares its shell with `AskUserFeedbackCard` — option rows, an "Other…" row
 * that becomes free text, one question per step — and differs in what the card
 * means: a run is stopped on this one, so it has no Skip. Every question it
 * asks must be answered before the run can resume.
 */
const THEME: AskUserCardTheme = ASK_USER_CARD_THEMES.input

export type AskUserQuestionProgress = AskUserCardProgress

interface AskUserQuestionCardProps {
  input: AskUserQuestionInput
  /**
   * The tool call id — used to post the tool-result back to resume the turn in
   * the *foreground* (inline-chat) flow. Optional because the background
   * (actions-panel) flow resolves the question via a staged-action endpoint and
   * supplies its own `onSubmit` instead.
   */
  toolCallId?: string
  conversationId?: string
  /** Existing tool-result, if the question was already answered (e.g. on reload). */
  existingResult?: string | null
  /**
   * Submission override. When provided, the card calls this instead of posting
   * a tool-result — used by the background actions-panel flow to answer a
   * pending "question" staged action. Must throw on failure so the card keeps
   * Submit visible for retry. When omitted, the card uses the inline
   * tool-result path (foreground chat).
   */
  onSubmit?: (answer: AskUserQuestionAnswer) => Promise<void>
  /**
   * Render without the outer `Cell` chrome (border/shadow/padding) so the card's
   * contents sit directly inside a host container. Used by the background drawer
   * to share one panel with the description, matching the view-action drawer.
   */
  bare?: boolean
  /**
   * Whether a bare card should render its own question header. Turn this off
   * when the staged-action header already owns the question title.
   */
  showBareHeader?: boolean
  /**
   * Let the body scroll inside a height-constrained bare host while the footer
   * stays pinned. The host bounds the height; the card only yields to it.
   */
  scrollableBody?: boolean
  progress?: AskUserQuestionProgress
  onProgress?: (answer: AskUserQuestionAnswer) => void
  /** Retires the card without answering; the footer's Skip calls it when set. */
  onDismiss?: () => void
}

/** Per-question working state, keyed by the question's `header`. */
interface QuestionState {
  selected: Set<string>
  /** The free-text "Other" value. Non-empty means it IS the answer. */
  other: string
  /** The optional "why" behind the pick. */
  explanation: string
}

/** The key `explanationAnchor` uses for the free-text row, which has no label. */
const OTHER_ROW = '__other'

const BODY_CLASS = 'flex min-w-0 basis-full flex-col gap-3'

/** Roughly three lines before the free-text box scrolls instead of growing. */
const OTHER_MAX_HEIGHT_PX = 132
/** The "why" box is a line of reasoning, not an essay. */
const EXPLANATION_MAX_HEIGHT_PX = 96

/**
 * The optional "why" behind an answer, rendered inside the row holding that
 * answer (Figma 8640:28023). It annotates a pick rather than replacing it,
 * which is what separates it from the "Other" row, and nesting is how the user
 * can tell which pick it belongs to.
 */
function ExplanationField({
  prompt,
  value,
  onChange,
  onSubmitRequest,
}: {
  prompt: string
  value: string
  onChange: (value: string) => void
  onSubmitRequest: () => void
}) {
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <span className="text-foreground-secondary cursor-text select-text text-[13px] leading-normal">
        {prompt} <span className="text-foreground-placeholder text-[13px]">(optional)</span>
      </span>
      <GrowingTextarea
        rows={1}
        maxHeight={EXPLANATION_MAX_HEIGHT_PX}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onSubmitRequest={onSubmitRequest}
        aria-label={`${prompt} (optional)`}
        placeholder="Share your reasoning…"
        // `text-foreground` explicitly: a base-layer rule colours every bare
        // textarea with the legacy `--text-primary`, whose dark value doesn't
        // apply in these windows, so the typed answer came out near-black.
        className="text-foreground placeholder:text-foreground-placeholder focus-visible:border-foreground/50 w-full resize-none rounded-md border bg-transparent p-2 text-[13px] leading-normal outline-none transition-colors duration-150"
      />
    </div>
  )
}

/**
 * One question and its answers: an option row per choice, then the "Other…" row
 * that becomes a text box when picked. Everything the question offers lives in
 * one group directly under it, so which answers belong to which question is
 * never in doubt.
 */
function QuestionStep({
  question,
  state,
  otherActive,
  showQuestionText,
  emphasizeQuestion,
  onToggleOption,
  onOtherActivate,
  onOtherChange,
  onExplanationChange,
  onSubmitRequest,
}: {
  question: AskUserQuestionItem
  state: QuestionState
  /** The Other row is the active answer, even while its text is still empty. */
  otherActive: boolean
  /**
   * Whether to render the question text above the options. False for a
   * single-question card whose question is promoted to the card title, so it
   * isn't said twice.
   */
  showQuestionText: boolean
  /** Questions yield emphasis to the card title when the agent supplied an intro. */
  emphasizeQuestion: boolean
  onToggleOption: (label: string) => void
  onOtherActivate: () => void
  onOtherChange: (value: string) => void
  onExplanationChange: (value: string) => void
  /** Enter in either free-text field advances or submits. */
  onSubmitRequest: () => void
}) {
  const otherFilled = state.other.trim().length > 0
  const showOtherBox = otherActive || otherFilled
  const otherRowRef = useRef<HTMLDivElement>(null)
  const explanationPrompt = question.explanationPrompt?.trim()
  const dragGuard = useRowDragGuard()

  // The "why" hangs off the first pick in the question's own order, or off the
  // typed answer when that is what was given. Anchoring to one row keeps a
  // multi-select from sprouting a box per pick, all writing to the same value.
  const explanationAnchor = explanationPrompt
    ? (question.options.find((option) => state.selected.has(option.label))?.label ?? (showOtherBox ? OTHER_ROW : null))
    : null

  // Focus lands in the box the moment the user picks the Other row, so picking
  // and typing is one motion. Deliberately keyed on the activation rather than
  // on mount — the card must not steal focus from the chat input.
  useEffect(() => {
    if (otherActive) otherRowRef.current?.querySelector('textarea')?.focus()
  }, [otherActive])

  const explanationFor = (anchor: string) =>
    explanationPrompt && explanationAnchor === anchor ? (
      <ExplanationField
        prompt={explanationPrompt}
        value={state.explanation}
        onChange={onExplanationChange}
        onSubmitRequest={onSubmitRequest}
      />
    ) : null

  return (
    <div className="flex min-w-0 flex-col gap-3">
      {showQuestionText && (
        <p
          className={twMerge(
            'text-foreground cursor-text select-text text-sm leading-normal',
            emphasizeQuestion ? 'font-medium' : 'font-normal',
          )}
        >
          {question.question}
        </p>
      )}

      <div role="group" aria-label={question.header} className="flex flex-col gap-2">
        {question.options.map((option, index) => {
          const selected = state.selected.has(option.label)
          const explanation = explanationFor(option.label)
          const label = (
            <>
              <OptionBadge letter={String.fromCharCode(65 + index)} selected={selected} theme={THEME} />
              {/* Selectable, unlike the badge above it. Copying an option
                  shouldn't drag its letter along. */}
              <span className="min-w-0 flex-1 select-text space-y-1">
                <span className="block text-sm leading-normal">{option.label}</span>
                {option.description && (
                  <span className="text-foreground-placeholder block text-[13px] leading-normal">
                    {option.description}
                  </span>
                )}
              </span>
            </>
          )
          const rowClass = twMerge(
            OPTION_ROW,
            OPTION_ROW_SURFACE,
            selected ? `border-border ${THEME.accentSelected}` : `${OPTION_ROW_IDLE} ${OPTION_ROW_PRESSABLE}`,
          )
          // A textarea inside a button is neither valid nor focusable, so a
          // row holding the "why" box becomes a container and the option line
          // inside it takes the click.
          if (explanation) {
            return (
              <div key={option.label} className={twMerge(rowClass, 'flex-col items-stretch gap-2')}>
                <button
                  type="button"
                  onPointerDown={dragGuard.onPointerDown}
                  onClick={(event) => {
                    if (!dragGuard.wasSelection(event)) onToggleOption(option.label)
                  }}
                  aria-pressed={selected}
                  // Inert chrome: the row around it already draws the selected
                  // fill, and a button left to its own background paints a grey
                  // band across the top of it.
                  className="flex w-full items-start gap-2 bg-transparent text-left text-inherit hover:bg-transparent"
                >
                  {label}
                </button>
                {explanation}
              </div>
            )
          }
          return (
            <button
              key={option.label}
              type="button"
              onPointerDown={dragGuard.onPointerDown}
              // Tapping the active option clears it, so a mis-tap is undoable.
              onClick={(event) => {
                if (!dragGuard.wasSelection(event)) onToggleOption(option.label)
              }}
              aria-pressed={selected}
              className={rowClass}
            >
              {label}
            </button>
          )
        })}

        {showOtherBox ? (
          <div
            ref={otherRowRef}
            className={twMerge(
              OPTION_ROW,
              OPTION_ROW_SURFACE,
              'flex-col items-stretch gap-2',
              otherFilled ? `border-border ${THEME.accentSelected}` : `border-border ${THEME.otherFocusBorder}`,
            )}
          >
            <div className="flex w-full items-start gap-2">
              <PencilLine aria-hidden="true" className="text-foreground-secondary mt-px size-3.5 shrink-0" />
              <GrowingTextarea
                rows={1}
                maxHeight={OTHER_MAX_HEIGHT_PX}
                value={state.other}
                onChange={(e) => onOtherChange(e.target.value)}
                onSubmitRequest={onSubmitRequest}
                aria-label="Other — type your own answer"
                placeholder="Type your own answer…"
                className="placeholder:text-foreground-placeholder text-foreground min-w-0 flex-1 resize-none bg-transparent text-sm leading-normal outline-none"
              />
            </div>
            {explanationFor(OTHER_ROW)}
          </div>
        ) : (
          <button
            type="button"
            onClick={onOtherActivate}
            className={twMerge(OPTION_ROW, OPTION_ROW_PRESSABLE, OPTION_ROW_DASHED)}
          >
            <PencilLine aria-hidden="true" className="text-foreground-secondary mt-px size-3.5 shrink-0" />
            <span className="text-foreground-secondary min-w-0 text-sm leading-normal">Other…</span>
          </button>
        )}
      </div>
    </div>
  )
}

/**
 * An answered question: everything the user chose as one compact settled row
 * (shared with the output view), labelled by its question only when the card
 * holds more than one — a single question is already the card's title.
 */
function ResolvedQuestion({
  question,
  answers,
  explanation,
  showQuestionText,
  emphasizeQuestion,
}: {
  question: AskUserQuestionItem
  answers: Array<string>
  explanation: string
  showQuestionText: boolean
  emphasizeQuestion: boolean
}) {
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      {showQuestionText && (
        <p
          className={twMerge(
            'text-foreground cursor-text select-text text-[13px] leading-normal',
            emphasizeQuestion ? 'font-medium' : 'font-normal',
          )}
        >
          {question.question}
        </p>
      )}
      <ResolvedAnswer answers={answers} explanation={explanation || undefined} />
    </div>
  )
}

export function AskUserQuestionCard({
  input,
  toolCallId,
  conversationId,
  existingResult,
  onSubmit,
  bare,
  showBareHeader = true,
  scrollableBody = false,
  progress,
  onProgress,
  onDismiss,
}: AskUserQuestionCardProps) {
  const submitToolResponse = useSubmitToolResponse()
  const { updateToolCall } = useMessagesStore()

  // Renders interactive when the result isn't our shape.
  const resolvedAnswer = useMemo(() => parseAskUserQuestionAnswer(existingResult), [existingResult])
  const [submittedAnswer, setSubmittedAnswer] = useState<AskUserQuestionAnswer | null>(resolvedAnswer)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  // One question at a time (Figma "Question 1 of 3") — the card renders beside
  // Brief content in the sidepanel and cannot stack four option lists.
  const [stepIndex, setStepIndex] = useState(0)
  const [maxVisitedStepIndex, setMaxVisitedStepIndex] = useState(0)
  const [otherActive, setOtherActive] = useState<Record<string, boolean>>({})

  const isResolved = submittedAnswer !== null

  const buildInitialState = useCallback(() => {
    const state: Record<string, QuestionState> = {}
    for (const q of input.questions) {
      const answer = resolvedAnswer?.answers.find((a) => a.header === q.header)
      const optionLabels = new Set(q.options.map((o) => o.label))
      const selected = new Set<string>()
      let other = ''
      if (answer) {
        for (const value of answer.selected) {
          if (optionLabels.has(value)) selected.add(value)
          else other = value // free-text "Other" value
        }
      }
      state[q.header] = { selected, other, explanation: answer?.explanation ?? '' }
    }
    return state
  }, [input.questions, resolvedAnswer])

  const [state, setState] = useState(buildInitialState)

  // A tool-result can arrive after mount (the turn resolves while this card is
  // already rendered). Sync the local answered state so the card becomes
  // read-only and a second submit is impossible — but only when the card was
  // not already answered locally, so an in-progress selection isn't clobbered.
  useEffect(() => {
    if (resolvedAnswer && !submittedAnswer) {
      setSubmittedAnswer(resolvedAnswer)
      setState(buildInitialState())
    }
  }, [resolvedAnswer, submittedAnswer, buildInitialState])

  const toggleOption = useCallback((header: string, label: string, multiSelect: boolean) => {
    setState((prev) => {
      const current = prev[header]
      const nextSelected = new Set(current.selected)
      if (nextSelected.has(label)) {
        nextSelected.delete(label)
      } else {
        if (!multiSelect) nextSelected.clear()
        nextSelected.add(label)
      }
      // Single-select: choosing an option clears the free-text "Other". The
      // "why" is left alone — wiping a sentence someone wrote is worse than
      // keeping a stale one they can still edit.
      const nextOther = multiSelect ? current.other : ''
      return { ...prev, [header]: { ...current, selected: nextSelected, other: nextOther } }
    })
    if (!multiSelect) setOtherActive((prev) => ({ ...prev, [header]: false }))
  }, [])

  const activateOther = useCallback((header: string, multiSelect: boolean) => {
    // Single-select: the typed answer replaces the pick, so drop it now rather
    // than leaving two answers showing until the first character lands.
    if (!multiSelect) {
      setState((prev) => ({ ...prev, [header]: { ...prev[header], selected: new Set<string>() } }))
    }
    setOtherActive((prev) => ({ ...prev, [header]: true }))
  }, [])

  const changeOther = useCallback((header: string, value: string, multiSelect: boolean) => {
    setState((prev) => {
      const current = prev[header]
      // Single-select: typing in "Other" clears any chosen option.
      const nextSelected = !multiSelect && value.trim().length > 0 ? new Set<string>() : current.selected
      return { ...prev, [header]: { ...current, selected: nextSelected, other: value } }
    })
  }, [])

  const changeExplanation = useCallback((header: string, value: string) => {
    setState((prev) => ({ ...prev, [header]: { ...prev[header], explanation: value } }))
  }, [])

  const totalSteps = input.questions.length
  const isLastStep = stepIndex >= totalSteps - 1
  const activeQuestion = input.questions[Math.min(stepIndex, totalSteps - 1)]

  const isAnswered = useCallback(
    (header: string) => {
      const s = state[header]
      return s.selected.size > 0 || s.other.trim().length > 0
    },
    [state],
  )

  // Every question must carry at least one selection (option or typed answer):
  // this card blocks a run, and a half-filled payload resumes it with a
  // decision nobody made.
  const allAnswered = useMemo(() => input.questions.every((q) => isAnswered(q.header)), [input.questions, isAnswered])

  const buildAnswer = useCallback((): AskUserQuestionAnswer => {
    return {
      answers: input.questions.map((q) => {
        const s = state[q.header]
        const selected = [...s.selected]
        const trimmedOther = s.other.trim()
        if (trimmedOther.length > 0) selected.push(trimmedOther)
        // Omitted rather than sent empty, so a skipped box reads as "no reason
        // given" instead of a reason that happens to be blank.
        const explanation = s.explanation.trim()
        return explanation.length > 0 ? { header: q.header, selected, explanation } : { header: q.header, selected }
      }),
    }
  }, [input.questions, state])

  useEffect(() => {
    if (!onProgress || maxVisitedStepIndex === 0 || isResolved) {
      return
    }
    const answer = buildAnswer()
    onProgress({ answers: answer.answers.slice(0, maxVisitedStepIndex) })
  }, [buildAnswer, isResolved, maxVisitedStepIndex, onProgress])

  const handleSubmit = useCallback(async () => {
    if (isSubmitting || isResolved || !allAnswered) return

    const answer = buildAnswer()

    setIsSubmitting(true)
    setSubmitError(null)

    try {
      if (onSubmit) {
        // Background (actions-panel) flow: caller answers the staged action.
        // It must throw on failure so we keep Submit visible for retry.
        await onSubmit(answer)
        setSubmittedAnswer(answer)
      } else {
        // Foreground (inline chat) flow: post the tool-result and resume the
        // turn. Guard: chat always has a conversationId/toolCallId, but never
        // crash if they're missing.
        if (!conversationId || !toolCallId) {
          console.error('AskUserQuestion: missing conversationId/toolCallId, cannot submit answer')
          setSubmitError('Failed to submit your answer. Please try again.')
          return
        }
        const content = JSON.stringify(answer)
        // The helper does NOT throw on a non-OK POST — it returns the Response —
        // so we must inspect `response.ok` to detect failure.
        const response = await submitToolResponse(conversationId, toolCallId, content)
        if (!response.ok) {
          throw new Error(`Failed to submit answer (status ${response.status})`)
        }
        // Only mark the card answered once the POST actually succeeded, so a
        // failure leaves Submit visible and the user can retry (mirrors useToolRunner).
        setSubmittedAnswer(answer)
        // The persisted result is what keeps a remounted card read-only.
        updateToolCall(conversationId, toolCallId, {
          status: 'success',
          result: content,
        })
      }
    } catch (error) {
      console.error('AskUserQuestion: failed to submit answer:', error)
      setSubmitError('Failed to submit your answer. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }, [
    isSubmitting,
    isResolved,
    allAnswered,
    conversationId,
    toolCallId,
    buildAnswer,
    updateToolCall,
    submitToolResponse,
    onSubmit,
  ])

  /** Enter and the primary button share this: advance mid-card, submit at the end. */
  const handleAdvance = useCallback(() => {
    if (!isAnswered(activeQuestion.header)) return
    if (isLastStep) {
      // A revisited step can be cleared after later ones were answered, and
      // `handleSubmit` bails on `!allAnswered` — jump to the blank step instead
      // of letting an enabled Submit silently do nothing.
      if (!allAnswered) {
        const firstUnanswered = input.questions.findIndex((q) => !isAnswered(q.header))
        if (firstUnanswered >= 0) setStepIndex(firstUnanswered)
        return
      }
      void handleSubmit()
    } else {
      const nextIndex = stepIndex + 1
      setMaxVisitedStepIndex((visitedIndex) => Math.max(visitedIndex, nextIndex))
      setStepIndex(nextIndex)
    }
  }, [isAnswered, activeQuestion.header, isLastStep, allAnswered, input.questions, handleSubmit, stepIndex])

  const progressTotal = progress?.total ?? totalSteps
  const progressIndex = (progress?.offset ?? 0) + stepIndex
  const isMulti = progressTotal > 1
  const showProgress = THEME.showProgress && isMulti && !isResolved
  const intro = input.intro?.trim()
  // A self-headed embedded action card promotes its active question to the
  // title. When a staged-action header owns the title instead, later steps
  // label themselves in the body so the static first-question title does not
  // obscure what the user is currently answering.
  const hasBareHeader = bare && showBareHeader
  const title =
    intro ??
    (hasBareHeader && !isResolved ? activeQuestion.question : isMulti ? 'A few questions' : activeQuestion.question)
  const showQuestionText = !!intro || (isMulti && (!bare || (!hasBareHeader && stepIndex > 0)))
  const emphasizeQuestion = !intro

  const header = (
    <Cell.Header showChevron={false}>
      <div className="flex items-center gap-2">
        {/* Bare cards use the same title hierarchy without the leading icon. */}
        {!bare && <AskUserBadge variant="input" />}
        <Cell.Title className="text-foreground line-clamp-none cursor-text select-text text-sm font-medium leading-normal">
          {title}
        </Cell.Title>
      </div>
    </Cell.Header>
  )

  // AnimatedHeight carries every content reflow — step changes, the free-text
  // box growing, the answers landing — so the card resizes instead of jumping
  // between heights. It measures before it transitions, so mount is static.
  const bodyContent = (
    <>
      {showProgress && (
        <StepProgress
          total={progressTotal}
          current={progressIndex}
          theme={THEME}
          onSelect={(index) => {
            const localIndex = index - (progress?.offset ?? 0)
            if (localIndex >= 0 && localIndex <= maxVisitedStepIndex) {
              setStepIndex(localIndex)
            }
          }}
          isStepEnabled={(index) => {
            const localIndex = index - (progress?.offset ?? 0)
            return localIndex >= 0 && localIndex <= maxVisitedStepIndex
          }}
        />
      )}
      {hasBareHeader && <div className={twMerge('flex min-w-0 flex-col gap-2', !showProgress && 'pt-1')}>{header}</div>}
      <AnimatedHeight>
        {isResolved ? (
          <div className="animate-in fade-in flex min-w-0 flex-col gap-3 duration-200">
            {input.questions.map((q) => {
              const s = state[q.header]
              const answers = [...s.selected]
              const trimmedOther = s.other.trim()
              if (trimmedOther.length > 0) answers.push(trimmedOther)
              return (
                <ResolvedQuestion
                  key={q.header}
                  question={q}
                  answers={answers}
                  explanation={s.explanation.trim()}
                  showQuestionText={!!intro || input.questions.length > 1}
                  emphasizeQuestion={emphasizeQuestion}
                />
              )
            })}
          </div>
        ) : (
          <QuestionStep
            key={activeQuestion.header}
            question={activeQuestion}
            state={state[activeQuestion.header]}
            otherActive={otherActive[activeQuestion.header] === true}
            showQuestionText={showQuestionText}
            emphasizeQuestion={emphasizeQuestion}
            onToggleOption={(label) => toggleOption(activeQuestion.header, label, activeQuestion.multiSelect)}
            onOtherActivate={() => activateOther(activeQuestion.header, activeQuestion.multiSelect)}
            onOtherChange={(value) => changeOther(activeQuestion.header, value, activeQuestion.multiSelect)}
            onExplanationChange={(value) => changeExplanation(activeQuestion.header, value)}
            onSubmitRequest={handleAdvance}
          />
        )}
      </AnimatedHeight>
    </>
  )

  // The padding keeps the rows' borders clear of the scroll box edge and the
  // resting mask.
  const body = scrollableBody ? (
    <FadingScrollable className={twMerge(BODY_CLASS, '-mx-0.5 min-h-0 px-0.5 py-0.5')}>{bodyContent}</FadingScrollable>
  ) : (
    <div className={BODY_CLASS}>{bodyContent}</div>
  )

  const statusLine =
    submitError ?? (isMulti ? `Question ${progressIndex + 1} of ${progressTotal}` : 'Select to continue')

  // No footer once resolved: the settled rows' check already says "answered",
  // matching the output view's compact treatment.
  const footer = !isResolved && (
    <Cell.Footer>
      <div className="flex w-full items-center justify-between gap-1.5">
        <span
          className={twMerge('text-foreground-placeholder min-w-0 text-[13px]', submitError && 'text-danger-subtle')}
        >
          {statusLine}
        </span>
        <div className="flex items-center gap-1.5">
          {onDismiss && (
            <Button size="sm" variant="ghost" onClick={onDismiss} disabled={isSubmitting}>
              Skip all
            </Button>
          )}
          <Button
            size="sm"
            variant="primary"
            onClick={handleAdvance}
            // Only greys out the affordance — `handleAdvance` and
            // `handleSubmit` hold the invariant. Skip retires the whole card;
            // the run can't continue on a partial answer.
            disabled={!isAnswered(activeQuestion.header) || isSubmitting}
            loading={isSubmitting}
            className={THEME.primaryButton}
          >
            {isLastStep && !progress?.hasFollowingSteps ? THEME.submitLabel : 'Next'}
          </Button>
        </div>
      </div>
    </Cell.Footer>
  )

  // Bare: drop the outer Cell chrome so the contents sit directly in a host
  // container (the drawer shares one panel with the description).
  if (bare) {
    return (
      <div className="flex min-h-0 flex-col gap-3">
        {body}
        {footer}
      </div>
    )
  }

  return (
    <Cell>
      {header}
      {body}
      {footer}
    </Cell>
  )
}
